<template>
  <div class="edit-webpage flex-grow-1 d-flex flex-column">
    <a-form
      id="edit-webpage-form"
      class="flex-grow-1"
      :form="editWebpageForm"
      @submit="onSubmit"
    >
      <div class="a-header">
        <span class="a-header--title">
          Intelligent Baseline Result
        </span>
      </div>
      <div class="a-body">
        <div class="sub-descriptions">
          <p class="mb-0">
            Baseline of your webpage is completed successfully.
          </p>
          <p class="mb-0">
            Please check the baseline settings below before saving the
            configuration.
          </p>
        </div>
        <div class="info">
          <a-row :gutter="20">
            <a-col :span="22">
              <a-form-item label="Webpage URL">
                <a-input v-decorator="['webPageUrl']" disabled />
              </a-form-item>
            </a-col>
            <a-col :span="2">
              <a-form-item label="Port">
                <a-input v-decorator="[`webPagePort`]" disabled />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="20">
            <a-col :span="8">
              <a-form-item>
                <template slot="label">
                  Webpage Name
                  <a-tooltip placement="top">
                    <template slot="title">
                      <span class="fs-10">
                        Please specify up to 40 alphanumeric characters for your
                        webpage name.
                      </span>
                    </template>
                    <a-icon type="info-circle" theme="filled" />
                  </a-tooltip>
                </template>
                <a-input
                  v-decorator="[
                    'webPageName',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please input webpage name',
                        },
                      ],
                    },
                  ]"
                />
              </a-form-item>
            </a-col>
            <a-col :span="8">
              <a-form-item label="Plan type">
                <a-input v-decorator="['planType']" disabled />
              </a-form-item>
            </a-col>
            <a-col :span="8">
              <a-form-item label="User Agent">
                <a-input v-decorator="['userAgentName']" disabled />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="20">
            <a-col :span="8">
              <a-form-item label="Polling Interval">
                <a-select v-decorator="['pollingInterval']" size="large">
                  <a-select-option
                    v-for="(pulling, keyPolling) in pullingIntervals"
                    :key="+keyPolling"
                    :value="+keyPolling"
                  >
                    {{ pulling }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="20">
            <a-col :span="12">
              <a-form-item label="Keyword(s) to include">
                <a-textarea
                  v-decorator="['keyWordToInclude']"
                  class="row-6 mb-0"
                  :rows="6"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Keyword(s) to exclude">
                <a-textarea
                  v-decorator="['keyWordToExclude']"
                  class="row-6 mb-0"
                  :rows="6"
                />
              </a-form-item>
            </a-col>
          </a-row>
        </div>
        <div class="tabs">
          <a-tabs type="card" :activeKey="activeTabKey" @change="onTabChange">
            <a-tab-pane key="1" tab="Content &amp; Integrity Analytics" v-if="featureAccess && featureAccess['content-integrity-engine']">
              <div ref="" class="mb-3">
                <a-form-item class="mb-0">
                  <a-checkbox
                    v-model="isEnableContentEngine"
                    @change="
                      (checkEvent) =>
                        onChangeEnableContentEngine(checkEvent.target.checked)
                    "
                  >
                    Enable Content &amp; Integrity Analytics
                    <a-tooltip placement="top">
                      <template slot="title">
                        <span class="fs-10">
                          <span>
                            The webpage is analyzed and base-lined for its
                            contents including the HTML title, number of links,
                            images, scripts, style sheets, etc.
                          </span>
                          <br />
                          <span>
                            Subsequent checks will be compared with the baseline
                            and the results fed into an advanced decision making
                            algorithm to determine if the webpage has been
                            modified.
                          </span>
                          <br />
                          <span>
                            A threshold can be set to allow for some deviation
                            (plus/minus) from the baseline value. The default
                            threshold value is 0.
                          </span>
                        </span>
                      </template>
                      <a-icon
                        class="explain"
                        type="info-circle"
                        theme="filled"
                      />
                    </a-tooltip>
                  </a-checkbox>
                </a-form-item>
              </div>
              <a-table
                rowKey="index"
                :rowSelection="
                  isEnableContentEngine
                    ? {
                        selectedRowKeys: selectedRowKeys,
                        onChange: onSelectChange,
                      }
                    : null
                "
                :columns="BaselineResultColumns"
                :dataSource="recommendedValues"
                :scroll="{ y: 800, x: 800 }"
                :pagination="recommendedValuesPagin"
              >
                <template slot="description" slot-scope="description">
                  <span>
                    {{ description }}
                    <a-tooltip
                      v-if="description === 'Full Page Signature'"
                      placement="top"
                    >
                      <template slot="title">
                        <span class="fs-10">
                          BETA: Detect changes by hashing the contents of the
                          entire page. Only use this feature on a static HTML
                          site.
                        </span>
                      </template>
                      <a-icon
                        class="explain"
                        type="info-circle"
                        theme="filled"
                      />
                    </a-tooltip>
                  </span>
                </template>
                <template
                  slot="recommendedValue"
                  slot-scope="recommendedValue, record"
                >
                  <span
                    v-if="baselineValueNotEditable.includes(record.description)"
                  >
                    {{ recommendedValue }}
                  </span>
                  <a-input
                    v-if="
                      !baselineValueNotEditable.includes(record.description)
                    "
                    type="text"
                    class="fit-input-width"
                    :defaultValue="recommendedValue"
                    :disabled="!selectedRowKeys.includes(record.index)"
                    @change="
                      (e) =>
                        handleChangeRecommendedValues(
                          e.target.value,
                          record.index,
                          'recommendedValue'
                        )
                    "
                  />
                </template>
                <template slot="threshold" slot-scope="threshold, record">
                  <a-input
                    v-show="threshold >= 0"
                    type="text"
                    class="fit-input-width"
                    :defaultValue="threshold"
                    :disabled="!selectedRowKeys.includes(record.index)"
                    @change="
                      (e) =>
                        handleChangeRecommendedValues(
                          e.target.value,
                          record.index,
                          'threshold'
                        )
                    "
                  />
                </template>

                <template slot="detail" slot-scope="text, record">
                  <a-dropdown
                    v-if="record.typeHasDetail"
                    v-model="record.visibleDropBox"
                    :placement="'bottomRight'"
                  >
                    <a-menu slot="overlay" class="detail-dropdown">
                      <a-menu-item
                        v-for="(item, index) in record.detail"
                        :key="item.id"
                        class="detail-item-readonly"
                      >
                        <a-tooltip placement="left">
                          <template slot="title">
                            <span class="fs-10">
                              {{ item.name }}
                            </span>
                          </template>
                          <a-checkbox
                            :disabled="!selectedRowKeys.includes(record.index)"
                            :defaultChecked="item.selected"
                            @change="
                              (e) =>
                                onClickCheckDetail(
                                  e.target.checked,
                                  record.index,
                                  index
                                )
                            "
                          >
                            {{ `${index + 1}.${item.name}` }}
                          </a-checkbox>
                          <a-button size="small" v-if="item.html" @click="onViewElementHtmlClicked(item)">
                            View HTML
                          </a-button>
                        </a-tooltip>
                      </a-menu-item>
                    </a-menu>
                    <a-button size="large" @click="(e) => e.preventDefault()">
                      View Detail <a-icon type="down" />
                    </a-button>
                  </a-dropdown>
                </template>
              </a-table>
            </a-tab-pane>
            <a-tab-pane key="2" tab="Preview HTML DOM">
                  <div class="sub-descriptions">
                    <span>
                      Review the baseline HTML content captured during the baselining process.
                    </span>
                  </div>
                  <PreviewHtml v-if="activeTabKey === '2'" :urlId="webpage.id" />
            </a-tab-pane>
            <a-tab-pane :key="ActivatedTabKeys.PreviewBaselineImage" tab="Preview Baseline Image">
                  <div class="sub-descriptions">
                    <span>
                      Review the baseline screenshot captured during the baselining process.
                    </span>
                  </div>
                  <PreviewBaselineImage
                    v-if="activeTabKey === ActivatedTabKeys.PreviewBaselineImage"
                    :urlId="webpage.id"
                  />
            </a-tab-pane>
          </a-tabs>
        </div>
      </div>
      <div class="a-footer">
        <a-button key="back" size="large" class="mr-3" @click="onCancel()">
          Cancel
        </a-button>
        <a-button key="submit" size="large" type="primary" html-type="submit">
          Save
        </a-button>
      </div>
    </a-form>
    <a-modal
            v-model="htmlModal.isVisible"
            :title="htmlModal.title"
            :footer="null"
    >
      <pre>{{ htmlModal.html }}</pre>
    </a-modal>
  </div>
</template>

<script>
import DataService from "@shared/services/data.service";
import {
  flatMapErrMessagesValidateForm,
  debounceTimeExecute,
} from "@shared/helpers/utils";
import WebpageService from "../services/webpage.service";
import { BaselineResultColumns } from "../helper.js";
import { get } from "lodash";
import PreviewHtml from "./PreviewHtml.vue";
import PreviewBaselineImage from "./PreviewBaselineImage.vue";
import { ActivatedTabKeys } from "../consts/Webpage.consts";

export default {
  name: "IntelligentBaselineResult",
  components: {
    PreviewHtml,
    PreviewBaselineImage,
  },
  data() {
    return {
      BaselineResultColumns,
      pullingIntervals: [],
      downtimeAlerts: [],
      webpage: {
        id: +this.$route.params.id,
      },
      content: {},
      recommendedValues: [],
      baselineValueNotEditable: ["Full Page Signature", "HTML Title"],
      selectedRowKeys: [],
      isEnableContentEngine: false,
      htmlModal: {
        isVisible: false,
        title: "",
        html: "",
      },
      ActivatedTabKeys,
      activeTabKey: "1",
    };
  },
  beforeCreate() {
    this.editWebpageForm = this.$form.createForm(this, {
      name: "edit-webpage-form",
      webPageUrl: "",
      webPagePort: "",
      webPageName: "",
      planType: "",
      userAgentId: "",
      userAgentName: "",
      downTimeAlert: "",
      pollingInterval: "",
      policy: "",
      isContinuousMonitor: false,
      isMonitorExtFile: false,
      keyWordToInclude: "",
      keyWordToExclude: "",
    });
  },
  mounted() {
    this.getWebpage();
  },
  computed: {
    featureAccess() {
      return this.$store.state.user.data.feature_access;
    },
    recommendedValuesPagin() {
      return {
        current: 1,
        pageSize: this.recommendedValues.length,
        total: this.recommendedValues.length,
      }
    }
  },
  methods: {
    onSubmit(e) {
      e.preventDefault();
      this.editWebpageForm.validateFields((err, values) => {
        if (!err) {
          const recommendedEditedValues = {
            title: this.recommendedValues.find(
              (item) => item.description === "HTML Title"
            ),
            line_num: this.recommendedValues.find(
              (item) => item.description === "# of HTML Lines"
            ),
            href: this.recommendedValues.find(
              (item) => item.description === "Href Links"
            ),
            img: this.recommendedValues.find(
              (item) => item.description === "Images"
            ),
            script: this.recommendedValues.find(
              (item) => item.description === "Javascripts"
            ),
            div: this.recommendedValues.find(
              (item) => item.description === "Divisions"
            ),
            iframe: this.recommendedValues.find(
              (item) => item.description === "Iframes"
            ),
            form: this.recommendedValues.find(
              (item) => item.description === "Forms"
            ),
            link: this.recommendedValues.find(
              (item) => item.description === "Cascading Styles Sheets"
            ),
            signature: this.recommendedValues.find(
              (item) => item.description === "Full Page Signature"
            ),
          };

          let recommendEditedCustomElementValues = [];
          for (const item of this.recommendedValues) {
            if (item.isCustomElement) {
              recommendEditedCustomElementValues.push({
                id: item.customElementRecommendationId,
                enable: item.enable,
                count: item.recommendedValue,
                threshold: item.threshold
              });
            }
          }

          const formatModelSaveEdit = {
            id: this.webpage && this.webpage.id,
            config_name: values.webPageName,
            plan_type: values.planType,
            polling_interval: values.pollingInterval,
            threshold: {
              threshold_line_num:
                (recommendedEditedValues.line_num &&
                  recommendedEditedValues.line_num.threshold) ||
                0,
              threshold_href:
                (recommendedEditedValues.href &&
                  recommendedEditedValues.href.threshold) ||
                0,
              threshold_img:
                (recommendedEditedValues.img &&
                  recommendedEditedValues.img.threshold) ||
                0,
              threshold_script:
                (recommendedEditedValues.script &&
                  recommendedEditedValues.script.threshold) ||
                0,
              threshold_div:
                (recommendedEditedValues.div &&
                  recommendedEditedValues.div.threshold) ||
                0,
              threshold_iframe:
                (recommendedEditedValues.iframe &&
                  recommendedEditedValues.iframe.threshold) ||
                0,
              threshold_form:
                (recommendedEditedValues.form &&
                  recommendedEditedValues.form.threshold) ||
                0,
              threshold_link:
                (recommendedEditedValues.link &&
                  recommendedEditedValues.link.threshold) ||
                0,
            },
            monitoring_configuration: {
              text_string_appear: values.keyWordToInclude || "",
              text_string_not_appear: values.keyWordToExclude || "",
            },
            recommend_baseline: {
              id:
                this.webpage.recommend_baseline &&
                this.webpage.recommend_baseline.id,
              title:
                this.webpage.recommend_baseline &&
                this.webpage.recommend_baseline.title,
              enable_content_engine: this.isEnableContentEngine,
              enable_title:
                this.isEnableContentEngine &&
                !!recommendedEditedValues.title &&
                !!recommendedEditedValues.title.enable,
              line_num:
                (recommendedEditedValues.line_num &&
                  recommendedEditedValues.line_num.recommendedValue) ||
                0,
              enable_line_num:
                this.isEnableContentEngine &&
                !!recommendedEditedValues.line_num &&
                !!recommendedEditedValues.line_num.enable,
              count_href:
                (recommendedEditedValues.href &&
                  recommendedEditedValues.href.recommendedValue) ||
                0,
              enable_count_href:
                this.isEnableContentEngine &&
                !!recommendedEditedValues.href &&
                !!recommendedEditedValues.href.enable,
              count_script:
                (recommendedEditedValues.script &&
                  recommendedEditedValues.script.recommendedValue) ||
                0,
              enable_count_script:
                this.isEnableContentEngine &&
                !!recommendedEditedValues.script &&
                !!recommendedEditedValues.script.enable,
              count_link:
                (recommendedEditedValues.link &&
                  recommendedEditedValues.link.recommendedValue) ||
                0,
              enable_count_link:
                this.isEnableContentEngine &&
                !!recommendedEditedValues.link &&
                !!recommendedEditedValues.link.enable,
              count_image:
                (recommendedEditedValues.img &&
                  recommendedEditedValues.img.recommendedValue) ||
                0,
              enable_count_image:
                this.isEnableContentEngine &&
                !!recommendedEditedValues.img &&
                !!recommendedEditedValues.img.enable,
              count_div:
                (recommendedEditedValues.div &&
                  recommendedEditedValues.div.recommendedValue) ||
                0,
              enable_count_div:
                this.isEnableContentEngine &&
                !!recommendedEditedValues.div &&
                !!recommendedEditedValues.div.enable,
              count_iframe:
                (recommendedEditedValues.iframe &&
                  recommendedEditedValues.iframe.recommendedValue) ||
                0,
              enable_count_iframe:
                this.isEnableContentEngine &&
                !!recommendedEditedValues.iframe &&
                !!recommendedEditedValues.iframe.enable,
              count_form:
                (recommendedEditedValues.form &&
                  recommendedEditedValues.form.recommendedValue) ||
                0,
              enable_count_form:
                this.isEnableContentEngine &&
                !!recommendedEditedValues.form &&
                !!recommendedEditedValues.form.enable,
              full_hash:
                this.webpage.recommend_baseline &&
                this.webpage.recommend_baseline.hash,
              enable_full_hash:
                this.isEnableContentEngine &&
                !!recommendedEditedValues.signature &&
                !!recommendedEditedValues.signature.enable,
              baseline_hrefs: (
                (this.webpage.recommend_baseline &&
                  this.webpage.recommend_baseline.baseline_hrefs) ||
                []
              ).map((baselineHrefItem) => ({
                id: baselineHrefItem.id,
                selected: baselineHrefItem.selected,
              })),
              baseline_iframes: (
                (this.webpage.recommend_baseline &&
                  this.webpage.recommend_baseline.baseline_iframes) ||
                []
              ).map((baselineIframeItem) => ({
                id: baselineIframeItem.id,
                selected: baselineIframeItem.selected,
              })),
              baseline_forms: (
                (this.webpage.recommend_baseline &&
                  this.webpage.recommend_baseline.baseline_forms) ||
                []
              ).map((baselineFormItem) => ({
                id: baselineFormItem.id,
                selected: baselineFormItem.selected,
              })),
              baseline_images: (
                (this.webpage.recommend_baseline &&
                  this.webpage.recommend_baseline.baseline_images) ||
                []
              ).map((baselineImageItem) => ({
                id: baselineImageItem.id,
                selected: baselineImageItem.selected,
              })),
              baseline_scripts: (
                (this.webpage.recommend_baseline &&
                  this.webpage.recommend_baseline.baseline_scripts) ||
                []
              ).map((baselineScriptItem) => ({
                id: baselineScriptItem.id,
                selected: baselineScriptItem.selected,
              })),
              baseline_stylesheets: (
                (this.webpage.recommend_baseline &&
                  this.webpage.recommend_baseline.baseline_stylesheets) ||
                []
              ).map((baselineStylesheetItem) => ({
                id: baselineStylesheetItem.id,
                selected: baselineStylesheetItem.selected,
              })),
              baseline_custom_elements: (
                  (this.webpage.recommend_baseline &&
                      this.webpage.recommend_baseline.baseline_custom_elements) ||
                  []
              ).map((baselineCustomElementItem) => ({
                  id: baselineCustomElementItem.id,
                  selected: baselineCustomElementItem.selected,
              })),
              baseline_custom_element_counts: recommendEditedCustomElementValues,
            },
          };
          WebpageService.updateBaselineSetting(
            +this.$route.params.id,
            formatModelSaveEdit
          )
            .then(() => {
              this.$showSuccessMessage("Updated Webpage configuration");
              this.$router.push("/webpages/list");
            })
            .catch((error) => {
              this.$showMultiErrorMessage(error.messages);
            });
        }
        if (err) {
          debounceTimeExecute(
            flatMapErrMessagesValidateForm(err),
            this.$showErrorMessage
          );
        }
      });
    },
    onCancel() {
      this.$router.push("/webpages/list");
    },
    setEditWebpageForm() {
      this.editWebpageForm.setFieldsValue({
        webPageUrl: this.webpage.url_name,
        webPagePort: this.webpage.monitoring_configuration.port_num,
        webPageName: this.webpage.config_name,
        planType: this.webpage.plan_type,
        userAgentName: this.webpage.monitoring_configuration.user_agent.alias,
        pollingInterval: this.webpage.polling_interval,
        keyWordToInclude: this.webpage.monitoring_configuration
          .text_string_appear,
        keyWordToExclude: this.webpage.monitoring_configuration
          .text_string_not_appear,
      });
    },
    getWebpage() {
      WebpageService.getRecommendations(this.webpage.id)
        .then((data) => {
          const threshold = get(data, "threshold") || {
            threshold_href: "",
            threshold_img: "",
            threshold_script: "",
            threshold_link: "",
            threshold_iframe: "",
            threshold_form: "",
            threshold_div: "",
            threshold_line_num: "",
          };
          const recommendedValue = get(data, "recommend_baseline", {});
          this.recommendedValues = [
            {
              enable: recommendedValue.enable_title,
              index: 1,
              description: "HTML Title",
              recommendedValue: recommendedValue.title,
              threshold: -1,
              detail: [],
              typeHasDetail: false,
              visibleDropBox: false,
            },
            {
              enable: recommendedValue.enable_count_href,
              index: 2,
              description: "Href Links",
              recommendedValue: recommendedValue.count_href,
              threshold: threshold.threshold_href,
              detail: recommendedValue.baseline_hrefs,
              typeHasDetail: true,
              visibleDropBox: false,
            },
            {
              enable: recommendedValue.enable_count_image,
              index: 3,
              description: "Images",
              recommendedValue: recommendedValue.count_image,
              threshold: threshold.threshold_img,
              detail: recommendedValue.baseline_images,
              typeHasDetail: true,
              visibleDropBox: false,
            },
            {
              enable: recommendedValue.enable_count_script,
              index: 4,
              description: "Javascripts",
              recommendedValue: recommendedValue.count_script,
              threshold: threshold.threshold_script,
              detail: recommendedValue.baseline_scripts,
              typeHasDetail: true,
              visibleDropBox: false,
            },
            {
              enable: recommendedValue.enable_count_link,
              index: 5,
              description: "Cascading Styles Sheets",
              recommendedValue: recommendedValue.count_link,
              threshold: threshold.threshold_link,
              detail: recommendedValue.baseline_stylesheets,
              typeHasDetail: true,
              visibleDropBox: false,
            },
            {
              enable: recommendedValue.enable_count_iframe,
              index: 6,
              description: "Iframes",
              recommendedValue: recommendedValue.count_iframe,
              threshold: threshold.threshold_iframe,
              detail: recommendedValue.baseline_iframes,
              typeHasDetail: true,
              visibleDropBox: false,
            },
            {
              enable: recommendedValue.enable_count_form,
              index: 7,
              description: "Forms",
              recommendedValue: recommendedValue.count_form,
              threshold: threshold.threshold_form,
              detail: recommendedValue.baseline_forms,
              typeHasDetail: true,
              visibleDropBox: false,
            },
            {
              enable: recommendedValue.enable_count_div,
              index: 7,
              description: "Divisions",
              recommendedValue: recommendedValue.count_div,
              threshold: threshold.threshold_div,
              detail: [],
              typeHasDetail: false,
              visibleDropBox: false,
            },
            {
              enable: recommendedValue.enable_line_num,
              index: 8,
              description: "# of HTML Lines",
              recommendedValue: recommendedValue.line_num,
              threshold: threshold.threshold_line_num,
              detail: [],
              typeHasDetail: false,
              visibleDropBox: false,
            },
            {
              enable: recommendedValue.enable_full_hash,
              index: 9,
              description: "Full Page Signature",
              recommendedValue: recommendedValue.full_hash.substring(10),
              threshold: -1,
              detail: [],
              typeHasDetail: false,
              visibleDropBox: false,
            },
          ];

          for (const customElementCount of recommendedValue.baseline_custom_element_counts) {
            console.log(customElementCount);
            this.recommendedValues.push({
              enable: customElementCount.enable,
              index: this.recommendedValues.length + 1,
              description: customElementCount.custom_element_definition ? "Custom Element: " + customElementCount.custom_element_definition.element_name : "Custom Element: [Deleted]",
              recommendedValue: customElementCount.count,
              threshold: 0,
              detail: recommendedValue.baseline_custom_elements.filter((element) => element.def_id === customElementCount.def_id),
              typeHasDetail: true,
              visibleDropBox: false,
              isCustomElement: true,
              customElementRecommendationId: customElementCount.id,
            })
          }

          this.webpage = data;
          this.setEditWebpageForm();
        })
        .then(() => {
          this.isEnableContentEngine =
            !!this.webpage.threshold &&
            !!this.webpage.threshold.enable_content_engine;
        })
        .then(() => {
          this.selectedRowKeys = this.isEnableContentEngine
            ? this.recommendedValues
                .filter((item) => item.enable)
                .map((item) => item.index)
            : [];
        })
        .finally(() => this.getMasterData());
    },
    getMasterData() {
      DataService.getEnums().then((enumsData) => {
        this.pullingIntervals =
          (enumsData &&
            enumsData.url &&
            enumsData.url.polling_interval[this.webpage.plan_type]) ||
          {};
        const pollingIntervalValue = this.editWebpageForm.getFieldValue(
          "pollingInterval"
        );
        if (!pollingIntervalValue) {
          const defaultValuePollingInterval = Math.min(
            ...Object.keys(this.pullingIntervals).map((key) => +key)
          );
          this.editWebpageForm.setFieldsValue({
            pollingInterval: defaultValuePollingInterval,
          });
        }
      });
    },
    handleChangeRecommendedValues(newValue, recordIndex, targetField) {
      const newData = [...this.recommendedValues];
      const findRecord = newData.find((item) => item.index === recordIndex);
      if (findRecord) {
        findRecord[targetField] = newValue;
        this.recommendedValues = newData;
      }
    },
    onSelectChange(selectedRowKeys) {
      this.selectedRowKeys = selectedRowKeys;
      this.recommendedValues = [...this.recommendedValues].map((item) => ({
        ...item,
        enable: selectedRowKeys.includes(item.index),
      }));
    },
    onClickCheckDetail(isChecked, recordIndex, detailIndex) {
      const newData = [...this.recommendedValues];
      const target = newData.find((item) => item.index === recordIndex);
      if (target && target.detail && target.detail.length) {
        target.detail[detailIndex].selected = isChecked;
        this.recommendedValues = newData;
      }
    },
    onChangeEnableContentEngine(isChecked) {
      this.selectedRowKeys = isChecked
        ? this.recommendedValues.map((item) => item.index)
        : [];
    },
    onViewElementHtmlClicked(item) {
      this.htmlModal.title = item.name;
      this.htmlModal.html = item.html;
      this.htmlModal.isVisible = true;
    },
    onTabChange(activeKey) {
      this.activeTabKey = activeKey;
    },
  },
};
</script>

<style lang="scss" scoped>
.a-body {
  padding: 20px;
  .info {
    background: #f7f7f7;
    overflow: hidden;
    padding: 20px;
    border-radius: 4px;
  }
  .tabs {
    margin-top: 10px;
    .ant-tabs-nav .ant-tabs-tab {
      padding-left: 0px !important;
    }
  }
}
.sub-descriptions {
  overflow: hidden;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 10px;
  background-color: #fffbe6;
  border: 1px solid #ffe58f;
}
.view-detail {
  ul {
    height: 500px;
    overflow: auto;
  }
}
.file-name {
  display: inline-block;
  width: 400px;
  white-space: nowrap;
  overflow: hidden !important;
  text-overflow: ellipsis;
}
.detail-item-readonly {
  cursor: default;
  .ant-checkbox-wrapper {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    width: 250px;
  }
}
.detail-dropdown {
  overflow: auto;
  height: auto;
  max-height: 400px;
}
.fit-input-width {
  width: 80px;
}
</style>
